import { useMemo } from "react";
import { alpha } from "@mui/material/styles";
import { Box, Typography } from "@mui/material";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
  ZAxis,
} from "recharts";
import {
  CHART_MARGIN,
  LIMITE_CRITICO,
  chartAxisProps,
  chartGridProps,
  formatInt,
  formatPct,
  pctColor,
  semaforoCores,
  tableHoverSx,
} from "./shared";
import RankingBarTableComponent from "../../../components/chartComponents/rankingBarTableComponent";

/* ----------------------- estatística ----------------------- */
// Limite inferior do intervalo de confiança de Wilson (95%, z=1.96).
// Ranqueia "de quem temos certeza que é ruim" em vez da % crua, corrigindo
// o viés de amostras pequenas (1 chamado = 100%).
export function wilsonLower(pos, n, z = 1.96) {
  if (!n) return 0;
  const p = pos / n;
  const z2 = z * z;
  const denom = 1 + z2 / n;
  const centro = p + z2 / (2 * n);
  const margem = z * Math.sqrt((p * (1 - p) + z2 / (4 * n)) / n);
  return Math.max((centro - margem) / denom, 0);
}

const mediana = (arr) => {
  if (!arr.length) return 0;
  const s = [...arr].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
};

/* ----------------------- A1: Matriz Volume × % (quadrante) ----------------------- */
export function QuadranteChart({ styles, theme, rows, getLabel }) {
  const { data, medX } = useMemo(() => {
    const data = rows.map((r) => ({
      x: r.volume || 0,
      y: +((r.pct || 0) * 100).toFixed(1),
      z: r.reinc || 0,
      label: getLabel(r),
    }));
    return {
      data,
      medX: mediana(data.map((d) => d.x)),
    };
  }, [rows, getLabel]);

  const rotuloRef = { fontSize: 10, fill: theme.palette.text.primary };
  // Corte horizontal = início da faixa crítica do semáforo (o mesmo das cores das bolhas); vertical = mediana de volume.
  const limiteY = LIMITE_CRITICO * 100;

  return (
    <Box sx={styles.chartBox}>
      {/* Topo e direita maiores para a bolha maior não ser cortada na borda. */}
      <ResponsiveContainer width="100%" height="100%">
        <ScatterChart margin={{ ...CHART_MARGIN, top: 20, right: 24, bottom: 8 }}>
          <CartesianGrid {...chartGridProps(theme)} />
          <XAxis
            type="number"
            dataKey="x"
            name="Volume"
            {...chartAxisProps(theme)}
            label={{
              value: "Volume",
              position: "insideBottomRight",
              offset: -4,
              fontSize: 10,
              fill: theme.palette.text.primary,
            }}
          />
          <YAxis
            type="number"
            dataKey="y"
            name="% Reinc."
            tickFormatter={(v) => `${v}%`}
            {...chartAxisProps(theme)}
          />
          <ZAxis type="number" dataKey="z" range={[40, 600]} name="Reinc." />
          {/* Quadrante de prioridade (volume acima da mediana + % crítica) tingido; as linhas vêm rotuladas. */}
          <ReferenceArea
            x1={medX}
            y1={limiteY}
            ifOverflow="extendDomain"
            fill={semaforoCores(theme).critico}
            fillOpacity={0.06}
            stroke="none"
            label={{ ...rotuloRef, value: "Prioridade", position: "insideTopRight" }}
          />
          <ReferenceLine
            x={medX}
            stroke={alpha(theme.palette.text.primary, 0.25)}
            strokeDasharray="4 4"
            label={{ ...rotuloRef, value: `mediana ${formatInt(medX)} chamados`, position: "top" }}
          />
          <ReferenceLine
            y={limiteY}
            ifOverflow="extendDomain"
            stroke={alpha(theme.palette.text.primary, 0.25)}
            strokeDasharray="4 4"
            label={{ ...rotuloRef, value: `crítico ${limiteY}%`, position: "insideTopLeft" }}
          />
          <RechartsTooltip
            cursor={{ strokeDasharray: "3 3" }}
            content={({ active, payload }) => {
              if (!active || !payload?.length) return null;
              const d = payload[0].payload;
              return (
                <Box sx={styles.chartTooltip}>
                  <Box sx={{ fontWeight: 700 }}>{d.label}</Box>
                  <Box>{d.y}% reincidência</Box>
                  <Box>
                    {formatInt(d.x)} chamados • {formatInt(d.z)} reinc.
                  </Box>
                </Box>
              );
            }}
          />
          <Scatter data={data} fillOpacity={0.7}>
            {data.map((d, i) => (
              <Cell
                key={i}
                fill={pctColor(theme, d.y / 100)}
                stroke={pctColor(theme, d.y / 100)}
              />
            ))}
          </Scatter>
        </ScatterChart>
      </ResponsiveContainer>
    </Box>
  );
}

/* ----------------------- A2: Ranking ajustado por Wilson ----------------------- */
export function WilsonBar({ styles, theme, rows, getLabel, topN = 10 }) {
  const data = useMemo(() => {
    return rows
      .map((r) => {
        const wilson = +(wilsonLower(r.reinc || 0, r.volume || 0) * 100).toFixed(1);
        const pct = +((r.pct || 0) * 100).toFixed(1);
        const volume = r.volume || 0;
        return {
          label: getLabel(r),
          labelTooltip: r.nome,
          value: wilson,
          // Cor pela faixa do próprio limite inferior (o "pelo menos X%").
          color: pctColor(theme, wilson / 100),
          tooltip: (
            <>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {getLabel(r)}
              </Typography>
              <Typography variant="body2">
                {`IC ${wilson}%  •  bruto ${pct}%  •  ${formatInt(volume)} chamados`}
              </Typography>
            </>
          ),
        };
      })
      .sort((a, b) => b.value - a.value)
      .slice(0, topN);
  }, [rows, getLabel, topN, theme]);

  return (
    <Box sx={{ ...styles.chartBox, ...tableHoverSx(theme) }}>
      <RankingBarTableComponent rows={data} valueFormatter={(v) => `${v}%`} />
    </Box>
  );
}

/* ----------------------- A5: Distribuição (histograma) ----------------------- */
export function DistribuicaoChart({ styles, theme, rows }) {
  const data = useMemo(() => {
    const bins = [
      { faixa: "0-10%", min: 0, max: 0.1, count: 0 },
      { faixa: "10-20%", min: 0.1, max: 0.2, count: 0 },
      { faixa: "20-30%", min: 0.2, max: 0.3, count: 0 },
      { faixa: "30-40%", min: 0.3, max: 0.4, count: 0 },
      { faixa: "40%+", min: 0.4, max: Infinity, count: 0 },
    ];
    rows.forEach((r) => {
      const p = r.pct || 0;
      const bin = bins.find((b) => p >= b.min && p < b.max);
      if (bin) bin.count += 1;
    });
    return bins;
  }, [rows]);

  return (
    <Box sx={styles.chartBox}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={CHART_MARGIN}>
          <CartesianGrid {...chartGridProps(theme)} />
          <XAxis dataKey="faixa" {...chartAxisProps(theme)} />
          <YAxis allowDecimals={false} {...chartAxisProps(theme)} />
          <RechartsTooltip
            contentStyle={styles.chartTooltip}
            itemStyle={styles.chartTooltipItem}
            labelStyle={styles.chartTooltipLabel}
            cursor={{ fill: alpha(theme.palette.text.primary, 0.04) }}
            formatter={(value) => [formatInt(value), "Qtd."]}
          />
          <Bar dataKey="count" radius={[2, 2, 0, 0]} maxBarSize={24}>
            {data.map((entry, i) => (
              <Cell key={i} fill={pctColor(theme, entry.min + 0.05)} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
}

/* re-export utilitário usado nos rankings */
export { formatPct, pctColor };
